import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  createHttpLink,
  split,
  from,
} from "@apollo/client"
import { GraphQLWsLink } from "@apollo/client/link/subscriptions"
import { getMainDefinition } from "@apollo/client/utilities"
import { setContext } from "@apollo/client/link/context"
import { RetryLink } from "@apollo/client/link/retry"
import { createClient } from "graphql-ws"

import { auth } from "services/firebase"
import { Header } from "types/hasura"
import { getFirebaseAuthToken } from "utils/server-storage"
import { contentfulClientName, contentfulLink } from "services/contentful"

const HASURA_API_ENDPOINT = process.env.GATSBY_HASURA_API_ENDPOINT || ""
const HASURA_API_WS_ENDPOINT = process.env.GATSBY_HASURA_API_WS_ENDPOINT || ""

// HTTP Link for queries and mutations
const httpLink = createHttpLink({
  uri: HASURA_API_ENDPOINT,
})

// WebSocket Link for subscriptions (browser only; Node/SSR has no WebSocket impl)
const wsLink =
  typeof window !== "undefined" && HASURA_API_WS_ENDPOINT
    ? new GraphQLWsLink(
        createClient({
          url: HASURA_API_WS_ENDPOINT,
          shouldRetry: () => true,
          retryAttempts: Infinity,
          connectionParams: async () => {
            try {
              const user = auth.currentUser
              if (!user) {
                return {}
              }

              const firebaseToken = await user.getIdToken()
              const tokenResult = await user.getIdTokenResult()

              if (firebaseToken && tokenResult?.claims) {
                return {
                  headers: {
                    Authorization: `Bearer ${firebaseToken}`,
                    "App-Version": "1.0.0",
                    "App-Build-Number": "1",
                  },
                }
              } else {
                return {}
              }
            } catch (error) {
              console.error("WebSocket connection error:", error)
              return {}
            }
          },
          on: {
            error: (error: unknown) => {
              console.error("WebSocket connection error:", error)
            },
          },
        })
      )
    : null

const authLink = setContext(async (_, { headers }) => {
  try {
    const user = auth.currentUser
    let firebaseToken = user ? await user.getIdToken() : null

    if (!firebaseToken) {
      const token = await getFirebaseAuthToken()
      firebaseToken = token || null
    }

    const tokenResult = user ? await user.getIdTokenResult() : null

    const hasHasuraClaims =
      tokenResult?.claims && tokenResult?.claims[Header.CLAIMS]

    const isLoggedIn = firebaseToken && hasHasuraClaims

    const config = {
      headers: {
        ...headers,
        ...(isLoggedIn
          ? {
              authorization: `Bearer ${firebaseToken}`,
            }
          : {}),
        "X-Client-Type": "gatsby-web",
        "X-Client-Version": process.env.GATSBY_APP_VERSION || "1.0.0",
      },
    }

    return config
  } catch (error) {
    console.error("HTTP auth error:", error)
    return { headers }
  }
})

// Retry Link for failed requests
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error: Error) => !!error,
  },
})

// Combine HTTP links
const httpLinkWithAuth = from([retryLink, httpLink])

// Split link: use WebSocket for subscriptions, HTTP for queries/mutations
const link = wsLink
  ? split(
      ({ query }) => {
        const definition = getMainDefinition(query)
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        )
      },
      wsLink,
      httpLinkWithAuth
    )
  : httpLinkWithAuth

export const hasuraLink =
  wsLink !== null
    ? split(
        ({ query }) => {
          const definition = getMainDefinition(query)
          return (
            definition.kind === "OperationDefinition" &&
            definition.operation === "subscription"
          )
        },
        wsLink,
        authLink.concat(link)
      )
    : authLink.concat(link)

// Route to Contentful when context.clientName === contentfulClientName (e.g. useCMS)
const linkWithContentfulSplit = ApolloLink.split(
  (operation) => operation.getContext().clientName === contentfulClientName,
  contentfulLink,
  hasuraLink
)

// Apollo Client instance
export const client = new ApolloClient({
  link: linkWithContentfulSplit,
  cache: new InMemoryCache({
    typePolicies: {},
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
    },
    query: {
      errorPolicy: "all",
    },
    mutate: {
      errorPolicy: "all",
    },
  },
  connectToDevTools: process.env.NODE_ENV === "development",
})
